import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Mail, CheckCircle, Loader2 } from 'lucide-react';

const GOOGLE_SHEETS_URL = "https://script.google.com/macros/s/AKfycbwhVGwYMTJtqwZVsUDGk9K_w4j-U_j1CVU5WXe8OW1zzrLr3uoMsP1JLP4W3bUYARe0/exec";

const WaitlistForm = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Detecta o dispositivo (Mobile ou Desktop)
  const getDeviceType = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/mobile|android|iphone|ipad|ipod|blackberry|windows phone/.test(userAgent)) {
      return 'Mobile';
    }
    return 'Desktop';
  };

  // Detecta o navegador
  const getBrowser = () => {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Edg')) return 'Edge';
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Opera') || userAgent.includes('OPR')) return 'Opera';
    return 'Outro';
  };

  // Detecta a origem do tráfego
  const getTrafficSource = () => {
    const referrer = document.referrer.toLowerCase();
    
    if (!referrer || referrer === '') return 'Direto';
    
    if (referrer.includes('google')) return 'Google';
    if (referrer.includes('instagram')) return 'Instagram';
    if (referrer.includes('facebook') || referrer.includes('fb.com')) return 'Facebook';
    if (referrer.includes('twitter') || referrer.includes('t.co')) return 'Twitter/X';
    if (referrer.includes('linkedin')) return 'LinkedIn';
    if (referrer.includes('youtube')) return 'YouTube';
    if (referrer.includes('tiktok')) return 'TikTok';
    if (referrer.includes('whatsapp')) return 'WhatsApp';
    if (referrer.includes('telegram')) return 'Telegram';
    
    // Retorna o domínio se não for uma fonte conhecida
    try {
      const url = new URL(referrer);
      return url.hostname.replace('www.', '');
    } catch {
      return 'Outro';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setStatus('error');
      setErrorMessage('Por favor, insira seu email');
      return;
    }

    if (!validateEmail(email)) {
      setStatus('error');
      setErrorMessage('Por favor, insira um email válido');
      return;
    }

    setStatus('loading');
    
    try {
      // Coleta informações automáticas
      const dados = {
        email: email,
        origem: getTrafficSource(),
        dispositivo: getDeviceType(),
        navegador: getBrowser()
      };

      // Envia direto para o Google Sheets
      await fetch(GOOGLE_SHEETS_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dados)
      });
      
      setTimeout(() => {
        setStatus('success');
        setEmail('');
      }, 1000);
      
    } catch (error) {
      console.error('Waitlist error:', error);
      setStatus('error');
      setErrorMessage('Erro ao cadastrar. Tente novamente.');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        type: 'spring',
        damping: 80,
        stiffness: 400,
        delay: 0.5
      }}
      className="w-full max-w-lg mx-auto"
    >
      <AnimatePresence mode="wait">
        {status === 'success' ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="flex flex-col items-center gap-4 p-8 rounded-3xl bg-white/80 backdrop-blur-sm shadow-lg border border-white/50"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 500, damping: 25, delay: 0.2 }}
              className="w-16 h-16 rounded-full bg-[#008055]/10 flex items-center justify-center"
            >
              <CheckCircle className="w-8 h-8 text-[#008055]" />
            </motion.div>
            <div className="text-center">
              <h3 className="text-xl font-semibold text-[#0A0A0A] mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                Você está na lista!
              </h3>
              <p className="text-[#3C3B3D]" style={{ fontFamily: 'Inter, sans-serif' }}>
                Avisaremos assim que o POSTAÍ estiver disponível.
              </p>
            </div>
            <motion.button
              onClick={() => setStatus('idle')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="mt-2 text-[#008055] font-medium hover:underline"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Cadastrar outro email
            </motion.button>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            onSubmit={handleSubmit}
            className="relative"
          >
            <div className="relative flex items-center">
              <div className="absolute left-5 text-[#AFADB2]">
                <Mail className="w-5 h-5" />
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (status === 'error') setStatus('idle');
                }}
                placeholder="Seu melhor email"
                className="w-full pl-14 pr-36 py-5 text-base rounded-full border border-[#E6E4EB] bg-white/90 backdrop-blur-sm text-[#0A0A0A] placeholder-[#AFADB2] outline-none transition-all duration-200 focus:border-[#008055] focus:ring-2 focus:ring-[#008055]/20 shadow-lg"
                style={{ fontFamily: 'Inter, sans-serif' }}
                disabled={status === 'loading'}
              />
              <motion.button
                type="submit"
                disabled={status === 'loading'}
                whileHover={{ scale: status === 'loading' ? 1 : 1.02 }}
                whileTap={{ scale: status === 'loading' ? 1 : 0.98 }}
                className="absolute right-2 px-6 py-3 rounded-full bg-[#0A6647] text-white font-medium text-base transition-colors hover:bg-[#008055] disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2 shadow-md"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {status === 'loading' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Enviando</span>
                  </>
                ) : (
                  <span>Entrar na lista</span>
                )}
              </motion.button>
            </div>
            <AnimatePresence>
              {status === 'error' && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-3 text-sm text-red-500 text-center"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {errorMessage}
                </motion.p>
              )}
            </AnimatePresence>
          </motion.form>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default WaitlistForm;
